import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ChevronRight, TrendingUp, Clock, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MobileLocationBar, getStoredLocationLabel } from "./MobileLocationBar";
import { categories } from "@/data/categories";
import { getCategoryImage } from "@/assets/categoryImages";
import { FeaturedProviders } from "@/components/FeaturedProviders";
import { HireProvidersBanner } from "@/components/HireProvidersBanner";


const TRENDING = ["Plumber", "Electrician", "AC Repair", "Cleaner", "Photographer", "Mechanic", "Hair Stylist", "Tailor"];

export function MobileHome() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [openAll, setOpenAll] = useState(false);

  const submit = (term: string) => {
    const params = new URLSearchParams();
    if (term) params.set("q", term);
    const loc = getStoredLocationLabel();
    if (loc && loc !== "Nigeria" && loc !== "Near me") params.set("location_label", loc);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div className="md:hidden">
      {/* Location strip */}
      <MobileLocationBar />

      {/* Search bar */}
      <div className="sticky top-16 z-30 bg-card px-4 py-3 shadow-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(q);
          }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search TaskRatel"
            className="h-11 rounded-full border-2 pl-10 pr-4"
          />
        </form>
      </div>

      {/* Horizontal category chips — scrollable, all categories */}
      <div className="border-b border-border bg-background">
        <div
          className="no-scrollbar flex w-full gap-3 overflow-x-auto px-4 py-3"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate(`/browse?category=${cat.id}`)}
              className="flex min-w-[80px] flex-shrink-0 flex-col items-center gap-1.5"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-3xl shadow-sm ring-1 ring-border">
                {cat.icon}
              </div>
              <span className="line-clamp-2 text-center text-xs font-semibold leading-tight text-foreground">
                {cat.name.split(" ")[0]}
              </span>
            </button>
          ))}

          <Sheet open={openAll} onOpenChange={setOpenAll}>
            <SheetTrigger asChild>
              <button className="flex min-w-[80px] flex-shrink-0 flex-col items-center gap-1.5">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30">
                  <ChevronRight className="h-7 w-7" />
                </div>
                <span className="text-xs font-semibold text-foreground">All</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] p-0">
              <SheetHeader className="border-b border-border p-4">
                <SheetTitle>All Categories</SheetTitle>
              </SheetHeader>
              <div
                className="no-scrollbar h-[calc(85vh-64px)] overflow-y-auto"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div className="grid grid-cols-3 gap-3 p-4">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/browse?category=${cat.id}`}
                      onClick={() => setOpenAll(false)}
                      className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-3"
                    >
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-3xl">
                        {cat.icon}
                      </div>
                      <span className="line-clamp-2 text-center text-xs font-semibold text-foreground">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

            </SheetContent>
          </Sheet>
        </div>
      </div>


      {/* Trending rail */}
      <section className="border-b border-border py-4">
        <div className="mb-3 flex items-center justify-between px-4">
          <h2 className="flex items-center gap-2 text-base font-bold">
            <TrendingUp className="h-4 w-4 text-primary" /> Trending near you
          </h2>
        </div>
        <div
          className="no-scrollbar flex w-full gap-2 overflow-x-auto px-4 pb-1"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {TRENDING.map((t) => (
            <button
              key={t}
              onClick={() => submit(t)}
              className="flex-shrink-0 whitespace-nowrap rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-primary"
            >
              🔥 {t}
            </button>
          ))}
        </div>
      </section>

      {/* Popular categories rail (image cards) */}
      <section className="border-b border-border py-4">
        <div className="mb-3 flex items-center justify-between px-4">
          <h2 className="text-base font-bold">Popular categories</h2>
          <Link to="/browse" className="text-xs font-medium text-primary">See all</Link>
        </div>
        <div
          className="no-scrollbar flex w-full gap-3 overflow-x-auto px-4 pb-1"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {categories.slice(0, 8).map((cat, index) => (
            <Link
              key={cat.id}
              to={`/browse?category=${cat.id}`}
              className="w-40 flex-shrink-0 overflow-hidden rounded-xl border border-border bg-card"
            >
              <div className="h-24 overflow-hidden">
                <img
                  src={getCategoryImage(cat.id)}
                  alt={cat.name}
                  width={160}
                  height={96}
                  loading={index < 2 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={index === 0 ? "high" : "auto"}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-2">
                <div className="line-clamp-1 text-sm font-semibold">{cat.name}</div>
                <div className="text-xs text-muted-foreground">
                  {cat.providerCount.toLocaleString()} pros
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Nearby providers */}
      <section className="pb-6">
        <div className="flex items-center justify-between px-4 pt-4">
          <h2 className="flex items-center gap-2 text-base font-bold">
            <MapPin className="h-4 w-4 text-primary" /> Nearby providers
          </h2>
        </div>
        <FeaturedProviders />
      </section>

      <HireProvidersBanner compact />

      {/* Recently viewed placeholder */}
      <section className="border-t border-border px-4 py-4">
        <h2 className="mb-2 flex items-center gap-2 text-base font-bold">
          <Clock className="h-4 w-4 text-primary" /> Recently viewed
        </h2>
        <p className="text-sm text-muted-foreground">
          Providers you view will show up here for quick access.
        </p>
      </section>
    </div>
  );
}